import type { SafetyTablesResponse } from './safetyTables';
import { resolveBaseUrl } from './apiBootstrap';
import { getAuthToken, unauthorised } from './authToken';
import { readErrorDetail } from './httpError';
import type { Compound } from './compounds';
import type { ModesResponse } from './modes';
import type { PanelsResponse } from './panels';
import type { BiomarkersResponse } from './biomarkers';
import type { SafetyIndexResponse } from './safetyIndex';
import type { TasksResponse } from './tasks';
import type { InstrumentsResponse } from './instruments';
import type { CpicCandidatesResponse } from './cpic';
import type { Invite, Tester } from './testers';
import type { UnmatchedBiomarker } from './unmatched';
import type { GenomicVariantsResponse } from './genomics';
import type { SourcesResponse } from './sources';
import type { InterventionClassesResponse, RenderersResponse } from './boardOutput';
import type { BetaGate, BetaGatesResponse, GateStatus, BetaDate, BetaFinding, BetaParticipant } from './betaConsole';
import type { OrderReferral, PathologyQueue } from './pathologyOrders';

/** Intake refused because the participant is excluded while the botanical
 *  safety gate is open. Distinct from a malformed request so the UI can say
 *  which it was — an exclusion is an answer, not an error. */
export class BetaExclusionRefusal extends Error {
  constructor(message: string) { super(message); this.name = 'BetaExclusionRefusal'; }
}

async function req<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(`${resolveBaseUrl()}${path}`, init);
  if (res.status === 401) throw unauthorised(path, init);
  if (!res.ok) throw new Error(await readErrorDetail(res, res.statusText));
  return (await res.json().catch(() => ({}))) as T;
}
function authedGet(): RequestInit {
  const t = getAuthToken();
  return { headers: t ? { Authorization: `Bearer ${t}` } : {} };
}
function authedJson(method: string, body: unknown): RequestInit {
  const t = getAuthToken();
  return {
    method,
    headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
    body: JSON.stringify(body),
  };
}

export type Crud<T, CreateBody = unknown, UpdateBody = CreateBody> = {
  list: () => Promise<T[]>;
  create: (body: CreateBody) => Promise<void>;
  update: (id: string, body: UpdateBody) => Promise<void>;
  remove: (id: string) => Promise<void>;
};

export function createCrud<T, CreateBody = unknown, UpdateBody = CreateBody>(
  basePath: string,
): Crud<T, CreateBody, UpdateBody> {
  const enc = (id: string) => `${basePath}/${encodeURIComponent(id)}`;
  return {
    list: () => req<T[]>(basePath, authedGet()),
    create: (body) => req<unknown>(basePath, authedJson('POST', body)).then(() => undefined),
    update: (id, body) => req<unknown>(enc(id), authedJson('PUT', body)).then(() => undefined),
    remove: (id) => req<unknown>(enc(id), { method: 'DELETE', headers: authedGet().headers }).then(() => undefined),
  };
}

// Read-only registries (GET-only backends: authored via seed + redeploy).
export type ReadOnly<T> = { list: () => Promise<T> };
export function createReadOnly<T>(path: string): ReadOnly<T> {
  return { list: () => req<T>(path, authedGet()) };
}

export type ModeDetail = { version: number | string | null; last_updated: string | null; mode: Record<string, unknown> };

export type CompoundCreateBody = { canonical_name: string; aliases: string[]; domains: string[] };
export type CompoundUpdateBody = { aliases: string[]; domains: string[] };

export const adminApi = {
  compounds: createCrud<Compound, CompoundCreateBody, CompoundUpdateBody>('/api/compounds'),
  modes: {
    list: () => req<ModesResponse>('/api/modes', authedGet()),
    get: (modeId: string) => req<ModeDetail>(`/api/modes/${encodeURIComponent(modeId)}`, authedGet()),
  },
  panels: createReadOnly<PanelsResponse>('/api/panels'),
  biomarkers: {
    list: () => req<BiomarkersResponse>('/api/biomarkers', authedGet()),
    // Canonicals are seed-controlled; only aliases are runtime-editable (admin only).
    // PUT overwrites/remaps the pattern → canonical; DELETE removes it.
    upsertAlias: (pattern: string, canonicalName: string) =>
      req<unknown>(`/admin/biomarker-aliases/${encodeURIComponent(pattern)}`,
        authedJson('PUT', { canonical_name: canonicalName, source: 'manual', confidence: 1.0 })).then(() => undefined),
    deleteAlias: (pattern: string) =>
      req<unknown>(`/admin/biomarker-aliases/${encodeURIComponent(pattern)}`,
        { method: 'DELETE', headers: authedGet().headers }).then(() => undefined),
  },
  tasks: createReadOnly<TasksResponse>('/api/tasks'),
  instruments: createReadOnly<InstrumentsResponse>('/api/instruments'),
  genomics: createReadOnly<GenomicVariantsResponse>('/api/genomics/variants'),
  sources: createReadOnly<SourcesResponse>('/api/sources'),
  interventionClasses: createReadOnly<InterventionClassesResponse>('/api/intervention-classes'),
  safetyTables: createReadOnly<SafetyTablesResponse>('/api/safety-tables'),
  renderers: createReadOnly<RenderersResponse>('/api/renderers'),
  invites: {
    list: () => req<{ invites: Invite[] }>('/admin/invites', authedGet()).then((r) => r.invites),
    // mode_id binds the invite to a product (dec-product-separation-invite-mode);
    // the backend validates it against the mode registry and redeem assigns the
    // invitee's active_mode from it. Required here because the user-facing mode
    // selector is gone (4b3cfc42) — a mode-less invite has nothing to fall back to.
    create: (invitee_email: string, mode_id: string) => req<{ code: string; invite_url: string; mode_id: string | null }>('/admin/invites', authedJson('POST', { invitee_email, mode_id })),
    // invite_url is optional and passed through from the list row, never minted
    // client-side; /send rebuilds it server-side from the code regardless.
    send: (code: string, to_email: string, invite_url?: string) => req<unknown>(`/admin/invites/${encodeURIComponent(code)}/send`, authedJson('POST', { to_email, invite_url })).then(() => undefined),
    revoke: (code: string) => req<unknown>(`/admin/invites/${encodeURIComponent(code)}/revoke`, authedJson('POST', {})).then(() => undefined),
  },
  testers: {
    list: () => req<{ testers: Tester[] }>('/admin/testers', authedGet()).then((r) => r.testers),
    revoke: (userRef: string) => req<unknown>(`/admin/testers/${encodeURIComponent(userRef)}/revoke`, authedJson('POST', {})).then(() => undefined),
    reinstate: (userRef: string) => req<unknown>(`/admin/testers/${encodeURIComponent(userRef)}/reinstate`, authedJson('POST', {})).then(() => undefined),
    /** Set (or clear) a tester's perimenopause-review cohort assignment.
     *  in_cohort:false removes them; the store validates the enums. */
    setCohort: (
      userRef: string,
      body: { in_cohort: boolean; review_mode: string; strata_stage: string; strata_subbranch: string },
    ) => req<unknown>(`/admin/testers/${encodeURIComponent(userRef)}/cohort`, authedJson('POST', body)).then(() => undefined),
  },
  // The unmatched-biomarker review queue (legacy /review's replacement). Resolving with
  // 'map' adds the alias found_name -> canonical_name for EVERY user, so it is admin-only
  // here and at core.
  unmatched: {
    list: (userRef: string, includeResolved = false) =>
      req<{ user_ref: string; unmatched: UnmatchedBiomarker[] }>(
        `/profile/${encodeURIComponent(userRef)}/unmatched${includeResolved ? '?include_resolved=true' : ''}`, authedGet(),
      ).then((r) => r.unmatched),
    approve: (id: number, canonicalName: string) =>
      req<unknown>(`/profile/unmatched/${id}/resolve`, authedJson('POST', { action: 'map', canonical_name: canonicalName })).then(() => undefined),
    dismiss: (id: number) =>
      req<unknown>(`/profile/unmatched/${id}/resolve`, authedJson('POST', { action: 'dismiss' })).then(() => undefined),
  },
  cpic: {
    candidates: () => req<CpicCandidatesResponse>('/api/cpic/candidates', authedGet()),
    fetch: () => req<unknown>('/api/cpic/fetch', authedJson('POST', {})),
    saveDraft: (id: string, body: { proposed_safety_entry: unknown; translation_notes?: string }) =>
      req<unknown>(`/api/cpic/candidates/${encodeURIComponent(id)}/save-draft`, authedJson('POST', body)).then(() => undefined),
    promote: (id: string, body: { proposed_safety_entry?: unknown; translation_notes?: string }) =>
      req<unknown>(`/api/cpic/candidates/${encodeURIComponent(id)}/promote`, authedJson('POST', body)).then(() => undefined),
    markNotRelevant: (id: string, reason: string) =>
      req<unknown>(`/api/cpic/candidates/${encodeURIComponent(id)}/mark-not-relevant`, authedJson('POST', { reason })).then(() => undefined),
    unlock: (id: string) =>
      req<unknown>(`/api/cpic/candidates/${encodeURIComponent(id)}/unlock`, authedJson('POST', {})).then(() => undefined),
  },
  // --- Perimenopause closed-beta console ---------------------------------
  // Preflight gates, timeline and findings for the beta run. Cohort strata and
  // the blinded clinical review are NOT here — they already exist under
  // /advisor/peri-review* (consoleApi.periReview). Contract agreed with PC.
  beta: {
    gates: {
      list: () => req<BetaGatesResponse>('/admin/beta/gates', authedGet()),
      history: (id: string) =>
        req<{ history: Array<{ to_status: string; note?: string; actor: string; timestamp: string }> }>(
          `/admin/beta/gates/${encodeURIComponent(id)}/history`, authedGet()).then((r) => r.history),
      // Only status and note are writable; label/detail/owner/blocking are seeded.
      patch: (id: string, body: { status?: GateStatus; note?: string }) =>
        req<unknown>(`/admin/beta/gates/${encodeURIComponent(id)}`, authedJson('PATCH', body)).then(() => undefined),
    },
    timeline: {
      list: () => req<{ dates: BetaDate[] }>('/admin/beta/timeline', authedGet()).then((r) => r.dates),
      // `date` is the only user-editable field — labels and notes are seeded.
      patch: (id: string, date: string) =>
        req<unknown>(`/admin/beta/timeline/${encodeURIComponent(id)}`, authedJson('PATCH', { date })).then(() => undefined),
    },
    findings: {
      list: () => req<{ findings: BetaFinding[] }>('/admin/beta/findings', authedGet()).then((r) => r.findings),
      create: (body: Pick<BetaFinding, 'cat' | 'severity' | 'src' | 'desc'> & { participant_ref?: string | null }) =>
        req<unknown>('/admin/beta/findings', authedJson('POST', body)).then(() => undefined),
      patch: (id: string, body: Partial<Pick<BetaFinding, 'status' | 'severity' | 'desc'>>) =>
        req<unknown>(`/admin/beta/findings/${encodeURIComponent(id)}`, authedJson('PATCH', body)).then(() => undefined),
      remove: (id: string) =>
        req<unknown>(`/admin/beta/findings/${encodeURIComponent(id)}`, { method: 'DELETE', headers: authedGet().headers }).then(() => undefined),
    },
    participants: {
      list: () => req<{ participants: BetaParticipant[] }>('/admin/beta/participants', authedGet()).then((r) => r.participants),
      patch: (participantRef: string, body: Partial<Omit<BetaParticipant, 'participant_ref'>>) =>
        req<unknown>(`/admin/beta/participants/${encodeURIComponent(participantRef)}`, authedJson('PATCH', body)).then(() => undefined),
      /** Enrol into the beta. The oestrogen-sensitive exclusion is refused
       *  SERVER-side while the botanical gate is open — 409, not 400: the
       *  request is well-formed and the participant is ineligible, which the UI
       *  must be able to tell apart. Rejects with BetaExclusionRefusal so the
       *  caller can say why rather than showing a generic failure. */
      enrol: async (userRef: string, oestrogenSensitive: boolean) => {
        const init = authedJson('POST', { user_ref: userRef, oestrogen_sensitive: oestrogenSensitive });
        const res = await fetch(`${resolveBaseUrl()}/admin/beta/participants`, init);
        if (res.status === 409) throw new BetaExclusionRefusal(await readErrorDetail(res, 'Excluded at intake.'));
        if (res.status === 401) throw unauthorised('/admin/beta/participants', init);
        if (!res.ok) throw new Error(await readErrorDetail(res, res.statusText));
      },
    },
  },
  // --- Pathology route A: the manual fallback (po-console-pathology-orders) ---
  // /admin/pathology/* (pathology/admin_api.py), admin only at the router: it
  // hands out people's referral details. placed and cancel are refused with 409
  // while a recipe run holds a live claim on the order; the message says until when.
  pathology: {
    queue: () => req<PathologyQueue>('/admin/pathology/queue', authedGet()),
    referral: (userRef: string, orderId: string) =>
      req<OrderReferral>(`/admin/pathology/orders/${encodeURIComponent(userRef)}/${encodeURIComponent(orderId)}/referral`, authedGet()),
    placed: (userRef: string, orderId: string, providerOrderId: string) =>
      req<unknown>(`/admin/pathology/orders/${encodeURIComponent(userRef)}/${encodeURIComponent(orderId)}/placed`,
        authedJson('POST', { provider_order_id: providerOrderId })).then(() => undefined),
    cancel: (userRef: string, orderId: string) =>
      req<unknown>(`/admin/pathology/orders/${encodeURIComponent(userRef)}/${encodeURIComponent(orderId)}/cancel`,
        authedJson('POST', {})).then(() => undefined),
    /** A held RESULT (not order): accept=false clears the hold so the next poll asks
     *  and checks again; accept=true confirms the held report is this account's.
     *  409 when there is no hold, or an accept on a hold that names no report. */
    releaseResult: (userRef: string, orderId: string, accept: boolean) =>
      req<unknown>(`/admin/pathology/orders/${encodeURIComponent(userRef)}/${encodeURIComponent(orderId)}/release-result`,
        authedJson('POST', { accept_report: accept })).then(() => undefined),
  },
  safetyIndex: {
    list: () => req<SafetyIndexResponse>('/api/safety-index', authedGet()),
    create: (entry: unknown) => req<unknown>('/api/safety-index', authedJson('POST', entry)).then(() => undefined),
    update: (entryId: string, entry: unknown) => req<unknown>(`/api/safety-index/${encodeURIComponent(entryId)}`, authedJson('PUT', entry)).then(() => undefined),
    remove: (entryId: string) => req<unknown>(`/api/safety-index/${encodeURIComponent(entryId)}`, { method: 'DELETE', headers: authedGet().headers }).then(() => undefined),
    toggle: (entryId: string) => req<unknown>(`/api/safety-index/${encodeURIComponent(entryId)}/toggle`, authedJson('POST', {})).then(() => undefined),
  },
};
